import {
  Box,
  type BoxProps,
  Card,
  CardActionArea,
  Divider,
  Stack,
  type Theme,
  Tooltip,
  Typography,
  cardActionAreaClasses,
  styled,
  touchRippleClasses,
  useTheme,
} from "@mui/material"
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  isValidElement,
} from "react"
import { Info } from "react-feather"

export type CardBottomColorLayoutProps = PropsWithChildren<{
  bottomColor?: CSSProperties["color"] | ((theme: Theme) => string)
  sx?: BoxProps["sx"]
  onClick?: () => void
  title?: ReactNode
  tooltip?: ReactNode
}>

const bottomHeight = "11px"

export const CardBottomColorLayout = ({
  title,
  tooltip,
  onClick,
  sx,
  bottomColor = "skyBlue",
  children,
}: CardBottomColorLayoutProps) => {
  const {
    palette: { primary },
    typography,
  } = useTheme()
  const hasAction = !!onClick

  return (
    <Card
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        pb: bottomHeight,
      }}
    >
      <CardActionArea
        onClick={onClick}
        disableRipple
        sx={{
          ...(hasAction
            ? {
                [`& > .${cardActionAreaClasses.focusHighlight},.${touchRippleClasses.child}`]:
                  {
                    bgcolor: bottomColor,
                  },
              }
            : {
                cursor: "unset",
                "&:hover": {
                  bgcolor: "transparent !important",
                  // Disable the overlay effect from MUI theme
                  "& .MuiCardActionArea-focusHighlight": {
                    opacity: "0 !important",
                  },
                },
              }),
          ...sx,
        }}
      >
        <Stack
          direction="column"
          divider={<Divider orientation="horizontal" flexItem />}
        >
          {title && (
            <Stack
              p={1.5}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {isValidElement(title) ? (
                title
              ) : (
                <Typography
                  textTransform="uppercase"
                  fontWeight={500}
                  sx={{ color: primary.dark1 }}
                >
                  {title}
                </Typography>
              )}
              {tooltip && (
                <Tooltip title={tooltip} placement="top-end">
                  <Info color={primary.light1} size={typography.pxToRem(20)} />
                </Tooltip>
              )}
            </Stack>
          )}
          <Box p={1.5}>{children}</Box>
        </Stack>
      </CardActionArea>
      <BottomBorder bgcolor={bottomColor} />
    </Card>
  )
}

// Avoid a border on the card to prevent height to be reduced
const BottomBorder = styled(Box)({
  height: bottomHeight,
  overflow: "visible",
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
})
